import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import betterflyLogo from '../../assets/betterfly-logo.png';
import { Badge } from '../ui';
import { cn } from '../ui/cn';
import { NAV_SECTIONS } from './navigation';

/**
 * Sidebar — grouped, collapsible navigation. 240px expanded / 64px collapsed.
 * Persists its collapsed state to localStorage (see Shell).
 */
export function Sidebar({ collapsed, onToggle, badges = {} }) {
  const location = useLocation();

  function isItemActive(item) {
    const { pathname } = location;
    if (item.path === '/') return pathname === '/';
    return pathname === item.path || pathname.startsWith(item.path + '/');
  }

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 64 : 240 }}
      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        'sticky top-0 z-30 flex h-screen shrink-0 flex-col self-start',
        'border-r border-line bg-surface',
      )}
    >
      {/* Brand + collapse toggle */}
      <div className="relative flex h-14 items-center border-b border-line px-3">
        <div className={cn('flex items-center', collapsed ? 'justify-center w-full' : 'gap-2')}>
          <div className={cn('shrink-0', collapsed ? 'h-7 w-7' : 'h-8 w-8')}>
            <img src={betterflyLogo} alt="Betterfly" className="h-full w-full object-contain" />
          </div>
          {!collapsed && (
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="text-body-sm font-semibold text-fg truncate">Betterfly</span>
              <span className="text-[10px] uppercase tracking-wider text-fg-subtle">
                Control
              </span>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={cn(
            'absolute -right-3 top-1/2 -translate-y-1/2',
            'inline-flex h-6 w-6 items-center justify-center rounded-full',
            'border border-line bg-surface text-fg-muted shadow-e1',
            'transition-colors hover:bg-ink-50 hover:text-fg',
            'focus-visible:outline-none focus-visible:shadow-focus-ring',
          )}
        >
          {collapsed ? <FiChevronsRight size={12} /> : <FiChevronsLeft size={12} />}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {NAV_SECTIONS.map((section) => (
          <div key={section.id} className="mb-4 last:mb-0">
            {!collapsed && (
              <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-fg-subtle">
                {section.label}
              </p>
            )}
            {collapsed && (
              <div className="mx-2 mb-1.5 h-px bg-line first:hidden" aria-hidden />
            )}
            <ul className="flex flex-col gap-0.5">
              {section.items.map((item) => (
                <li key={item.id}>
                  <NavLink
                    item={item}
                    active={isItemActive(item)}
                    collapsed={collapsed}
                    badge={item.badgeKey ? badges[item.badgeKey] : undefined}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </motion.aside>
  );
}

function NavLink({ item, active, collapsed, badge }) {
  const Icon = item.icon;
  const [label, ...shortcutParts] = item.shortcut ? item.shortcut.split(' ') : [];
  const shortcutDisplay = item.shortcut
    ? `${label.toUpperCase()} ${shortcutParts.join('').toUpperCase()}`
    : null;

  return (
    <Link
      to={item.path}
      title={collapsed ? item.label : undefined}
      className={cn(
        'group relative flex items-center gap-2.5 rounded-md px-2 h-8',
        'text-body-sm transition-colors',
        'focus-visible:outline-none focus-visible:shadow-focus-ring',
        active
          ? 'bg-brand-50 text-brand-700 font-semibold'
          : 'text-fg-muted hover:bg-ink-50 hover:text-fg',
        collapsed && 'justify-center px-0',
      )}
    >
      {active && !collapsed && (
        <span
          aria-hidden
          className="absolute left-0 top-1/2 h-5 w-[2px] -translate-y-1/2 rounded-r-full bg-brand-600"
        />
      )}
      <Icon size={16} className="shrink-0" aria-hidden />
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {badge != null && badge > 0 && (
            <Badge variant={active ? 'brand' : 'warning'} size="sm">
              {badge > 99 ? '99+' : badge}
            </Badge>
          )}
          {item.shortcut && !badge && (
            <span className="hidden font-mono text-[10px] text-fg-subtle group-hover:inline">
              {shortcutDisplay}
            </span>
          )}
        </>
      )}
      {collapsed && badge != null && badge > 0 && (
        <span
          aria-hidden
          className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-warning-500 ring-2 ring-surface"
        />
      )}
    </Link>
  );
}

export default Sidebar;
